import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Camera, RefreshCw, Download, Share2, X } from 'lucide-react';
import PhotoManager from './PhotoManager';
import './VirtualTryOn.scss';

/**
 * TryOn Menu Overlay
 * Appears on top of the try-on image with action buttons
 * - Manage Photos (opens photo manager modal)
 * - Regenerate Try-On
 * - Download Image
 * - Share Result
 */
const TryOnMenuOverlay = ({ onRegenerate, tryOnImage, onClose }) => {
  const { t } = useTranslation();
  const [isPhotoManagerOpen, setIsPhotoManagerOpen] = useState(false);

  const handleDownload = () => {
    if (!tryOnImage) return;

    // Create a temporary link to download the image
    const link = document.createElement('a');
    link.href = tryOnImage;
    link.download = `likiee-tryon-${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async () => {
    if (!tryOnImage) return;

    // Check if Web Share API is available
    if (navigator.share) {
      try {
        // Convert base64 to blob for sharing
        const response = await fetch(tryOnImage);
        const blob = await response.blob();
        const file = new File([blob], 'likiee-tryon.png', { type: 'image/png' });

        await navigator.share({
          title: t('My Virtual Try-On'),
          text: t('Check out my virtual try-on result from Likiee!'),
          files: [file],
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    } else {
      // Fallback: Copy link or show share options
      alert(t('Sharing is not supported on this device'));
    }
  };

  return (
    <>
      {/* Expandable Menu Bar (expands from button) */}
      <div className='tryon-menu-expand'>
        {/* Manage Photos */}
        <button
          className='tryon-menu-expand__item'
          onClick={() => setIsPhotoManagerOpen(true)}
          aria-label={t('Manage Photos')}
        >
          <Camera className='tryon-menu-expand__icon' size={18} />
          <span className='tryon-menu-expand__text'>{t('Manage')}</span>
        </button>

        {/* Regenerate */}
        <button className='tryon-menu-expand__item' onClick={onRegenerate} aria-label={t('Regenerate')}>
          <RefreshCw className='tryon-menu-expand__icon' size={18} />
          <span className='tryon-menu-expand__text'>{t('Regenerate')}</span>
        </button>

        {/* Download */}
        <button className='tryon-menu-expand__item' onClick={handleDownload} aria-label={t('Download')}>
          <Download className='tryon-menu-expand__icon' size={18} />
          <span className='tryon-menu-expand__text'>{t('Download')}</span>
        </button>

        {/* Share */}
        <button className='tryon-menu-expand__item' onClick={handleShare} aria-label={t('Share')}>
          <Share2 className='tryon-menu-expand__icon' size={18} />
          <span className='tryon-menu-expand__text'>{t('Share')}</span>
        </button>

        {/* Close */}
        <button
          className='tryon-menu-expand__item tryon-menu-expand__item--close'
          onClick={onClose}
          aria-label={t('Close')}
        >
          <X className='tryon-menu-expand__icon' size={18} />
        </button>
      </div>

      {/* Photo Manager Modal */}
      {isPhotoManagerOpen &&
        createPortal(
          <div className='photo-manager-modal'>
            <div
              className='photo-manager-modal__overlay'
              onClick={() => setIsPhotoManagerOpen(false)}
              role='button'
              tabIndex={0}
              onKeyDown={e => {
                if (e.key === 'Escape' || e.key === 'Enter') {
                  setIsPhotoManagerOpen(false);
                }
              }}
              aria-label='Close modal'
            />
            <div className='photo-manager-modal__content'>
              <button
                className='photo-manager-modal__close'
                onClick={() => setIsPhotoManagerOpen(false)}
                aria-label={t('Close')}
              >
                <X size={24} />
              </button>
              <PhotoManager />
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};

TryOnMenuOverlay.propTypes = {
  onRegenerate: PropTypes.func.isRequired,
  tryOnImage: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default TryOnMenuOverlay;
